import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import {
  Award, Plus, Trash2, Search, Upload, FileText, X, ChevronDown,
  BookOpen, User, Phone, Hash, MessageSquare, Shield, ExternalLink,
  CheckCircle, Trophy, ClipboardList, Eye, Image as ImageIcon, Paperclip, Sparkles
} from 'lucide-react';

const STAGE_BADGES = {
  written_cleared: { label: 'Written Cleared', cls: 'bg-amber-100 text-amber-800' },
  final_selection: { label: 'Final Selection', cls: 'bg-emerald-100 text-emerald-800' },
};

// ─── helpers for rendering student-submitted custom field values ──────────────
const isUrl = (v) => typeof v === 'string' && /^https?:\/\//i.test(v);
const isImageUrl = (v) => isUrl(v) && /\.(png|jpe?g|gif|webp|bmp|svg)(\?.*)?$/i.test(v);

const renderCustomValue = (val) => {
  if (val === null || val === undefined || val === '') return <span className="text-slate-300">—</span>;
  const s = String(val);
  if (isImageUrl(s)) {
    return (
      <a href={s} target="_blank" rel="noopener noreferrer" className="inline-block group">
        <img src={s} alt="upload" className="h-20 w-20 object-cover rounded-lg border border-slate-200 group-hover:border-emerald-400 transition" />
      </a>
    );
  }
  if (isUrl(s)) {
    return (
      <a href={s} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-blue-50 text-blue-700 rounded-md text-xs font-medium hover:bg-blue-100">
        <Paperclip size={12} /> View file
      </a>
    );
  }
  return <span className="text-slate-800 break-words">{s}</span>;
};

const DetailsRow = ({ label, children }) => (
  <div className="grid grid-cols-3 gap-3 py-2.5 border-b border-slate-100 last:border-0">
    <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{label}</div>
    <div className="col-span-2 text-sm">{children}</div>
  </div>
);

const ResultDetailsModal = ({ result, onClose }) => {
  if (!result) return null;
  const customFields = result.custom_fields || {};
  const customEntries = Object.entries(customFields).filter(([, v]) => v !== '' && v !== null && v !== undefined);

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="result-details-modal"
      >
        <div className="sticky top-0 bg-gradient-to-r from-emerald-600 to-emerald-700 text-white px-6 py-4 flex items-center justify-between rounded-t-xl">
          <div>
            <h2 className="text-lg font-bold">{result.name}</h2>
            <p className="text-emerald-100 text-xs">{result.exam_name} · {result.contact_number}</p>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-white/15 rounded-lg transition" data-testid="close-result-details">
            <X size={20} />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {/* Built-in / standard fields */}
          <section>
            <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2 flex items-center gap-1.5">
              <ClipboardList size={13} /> Standard Details
            </h3>
            <div className="bg-slate-50 rounded-lg px-4 divide-y divide-slate-100">
              <DetailsRow label="Name">{result.name}</DetailsRow>
              <DetailsRow label="Contact">{result.contact_number}</DetailsRow>
              <DetailsRow label="Exam">{result.exam_name}</DetailsRow>
              <DetailsRow label="Stage">
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${(STAGE_BADGES[result.stage] || STAGE_BADGES.written_cleared).cls}`}>
                  {(STAGE_BADGES[result.stage] || STAGE_BADGES.written_cleared).label}
                </span>
              </DetailsRow>
              <DetailsRow label="Rank">{result.rank || <span className="text-slate-300">—</span>}</DetailsRow>
              <DetailsRow label="Roll No.">{result.roll_number || <span className="text-slate-300">—</span>}</DetailsRow>
              <DetailsRow label="Remarks">{result.remarks || <span className="text-slate-300">—</span>}</DetailsRow>
              <DetailsRow label="Source">
                {result.source === 'public_form'
                  ? <span className="px-1.5 py-0.5 bg-purple-50 text-purple-700 rounded text-xs">Submitted by Student</span>
                  : (result.verified_by ? `Admin · ${result.verified_by}` : 'Admin Entry')}
              </DetailsRow>
              <DetailsRow label="Scorecard">
                {result.document_url ? (
                  isImageUrl(result.document_url) ? (
                    <a href={result.document_url} target="_blank" rel="noopener noreferrer">
                      <img src={result.document_url} alt="scorecard" className="h-24 rounded-lg border border-slate-200 hover:border-emerald-400 transition" />
                    </a>
                  ) : (
                    <a href={result.document_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-blue-50 text-blue-700 rounded-md text-xs font-medium hover:bg-blue-100">
                      <ExternalLink size={12} /> {result.document_name || 'View document'}
                    </a>
                  )
                ) : <span className="text-slate-300">—</span>}
              </DetailsRow>
              <DetailsRow label="Submitted">{result.created_at ? new Date(result.created_at).toLocaleString() : '—'}</DetailsRow>
            </div>
          </section>

          {/* Custom fields */}
          <section>
            <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2 flex items-center gap-1.5">
              <Sparkles size={13} /> Student-Submitted Details
              {customEntries.length > 0 && (
                <span className="ml-1 px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded-full text-[10px] font-bold">
                  {customEntries.length}
                </span>
              )}
            </h3>
            {customEntries.length === 0 ? (
              <div className="text-sm text-slate-400 italic px-4 py-3 bg-slate-50 rounded-lg border border-dashed border-slate-200">
                No additional fields were filled by the student.
              </div>
            ) : (
              <div className="bg-purple-50/40 rounded-lg px-4 divide-y divide-purple-100/60">
                {customEntries.map(([k, v]) => (
                  <DetailsRow key={k} label={k}>{renderCustomValue(v)}</DetailsRow>
                ))}
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
};

const Results = () => {
  const [tab, setTab] = useState('results'); // 'results' | 'exams'
  const [exams, setExams] = useState([]);
  const [results, setResults] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, total: 0, total_pages: 1 });
  const [search, setSearch] = useState('');
  const [filterExam, setFilterExam] = useState('');
  const [filterStage, setFilterStage] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingResult, setEditingResult] = useState(null);
  const [newExamName, setNewExamName] = useState('');
  const [formData, setFormData] = useState({
    name: '', contact_number: '', exam_id: '', rank: '', roll_number: '', remarks: ''
  });
  const [documentFile, setDocumentFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [detailsResult, setDetailsResult] = useState(null);

  const fetchExams = useCallback(async () => {
    try {
      const res = await api.getResultExams();
      setExams(res.data.exams || []);
    } catch { /* ignore */ }
  }, []);

  const fetchResults = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      const params = { page, limit: 50 };
      if (filterExam) params.exam_id = filterExam;
      if (filterStage) params.stage = filterStage;
      if (search) params.search = search;
      const res = await api.getResults(params);
      setResults(res.data.results || []);
      setPagination(res.data.pagination || { page: 1, total: 0, total_pages: 1 });
    } catch {
      toast.error('Failed to load results');
    } finally {
      setLoading(false);
    }
  }, [filterExam, filterStage, search]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await api.getResultsStats();
      setStats(res.data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { fetchExams(); fetchStats(); }, [fetchExams, fetchStats]);
  useEffect(() => { fetchResults(1); }, [fetchResults]);

  const handleAddExam = async () => {
    if (!newExamName.trim()) return;
    try {
      await api.createResultExam({ name: newExamName.trim() });
      toast.success('Exam added');
      setNewExamName('');
      fetchExams();
      fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add exam');
    }
  };

  const handleDeleteExam = async (id, name) => {
    if (!window.confirm(`Delete exam "${name}"?`)) return;
    try {
      await api.deleteResultExam(id);
      toast.success('Exam deleted');
      fetchExams();
      fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete exam');
    }
  };

  const resetForm = () => {
    setFormData({ name: '', contact_number: '', exam_id: '', rank: '', roll_number: '', remarks: '' });
    setDocumentFile(null);
    setEditingResult(null);
    setShowForm(false);
  };

  const openEditForm = (result) => {
    setEditingResult(result);
    setFormData({
      name: result.name || '',
      contact_number: result.contact_number || '',
      exam_id: result.exam_id || '',
      rank: result.rank || '',
      roll_number: result.roll_number || '',
      remarks: result.remarks || ''
    });
    setDocumentFile(null);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.contact_number || !formData.exam_id) {
      toast.error('Name, Contact Number, and Exam are required');
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('name', formData.name);
      fd.append('contact_number', formData.contact_number);
      fd.append('exam_id', formData.exam_id);
      if (formData.rank) fd.append('rank', formData.rank);
      if (formData.roll_number) fd.append('roll_number', formData.roll_number);
      if (formData.remarks) fd.append('remarks', formData.remarks);
      if (documentFile) fd.append('document', documentFile);

      if (editingResult) {
        await api.updateResult(editingResult.id, fd);
        toast.success('Result updated');
      } else {
        await api.createResult(fd);
        toast.success('Result added');
      }
      resetForm();
      fetchResults(pagination.page);
      fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save result');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteResult = async (id) => {
    if (!window.confirm('Delete this result entry?')) return;
    try {
      await api.deleteResult(id);
      toast.success('Result deleted');
      fetchResults(pagination.page);
      fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleStageChange = async (r, newStage) => {
    const label = newStage === 'final_selection' ? 'Mark as FINAL SELECTION' : 'Move back to Written Cleared';
    if (!window.confirm(`${label} for ${r.name}?`)) return;
    try {
      await api.updateResultStage(r.id, { stage: newStage });
      toast.success(`Stage updated`);
      fetchResults(pagination.page);
      fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update stage');
    }
  };

  return (
    <div data-testid="results-module" className="max-w-[1400px] mx-auto space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 uppercase tracking-wide">Results Module</h1>
          <p className="text-slate-500 text-sm">Track exam qualifications and verified results</p>
        </div>
        <button
          data-testid="add-result-btn"
          onClick={() => { resetForm(); setShowForm(true); }}
          className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition font-medium"
        >
          <Plus size={18} /> Add Result
        </button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm">
            <p className="text-xs text-slate-400 uppercase tracking-wider">Total Entries</p>
            <p className="text-2xl font-bold text-slate-900">{stats.total_results}</p>
          </div>
          <div className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-xl p-4 border border-amber-200">
            <p className="text-xs text-amber-700 uppercase tracking-wider font-semibold">Written Cleared</p>
            <p className="text-2xl font-bold text-amber-800">{stats.total_written_cleared || 0}</p>
          </div>
          <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-xl p-4 border border-emerald-200">
            <p className="text-xs text-emerald-700 uppercase tracking-wider font-semibold">Final Selections</p>
            <p className="text-2xl font-bold text-emerald-800">{stats.total_final_selections || 0}</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm">
            <p className="text-xs text-slate-400 uppercase tracking-wider">Total Exams</p>
            <p className="text-2xl font-bold text-slate-900">{stats.total_exams}</p>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 bg-slate-100 p-1 rounded-lg w-fit">
        <button
          data-testid="tab-results"
          onClick={() => setTab('results')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition ${
            tab === 'results' ? 'bg-white text-slate-900 shadow' : 'text-slate-500 hover:text-slate-700'
          }`}
        >
          <Award size={16} className="inline mr-1.5 -mt-0.5" /> Results
        </button>
        <button
          data-testid="tab-exams"
          onClick={() => setTab('exams')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition ${
            tab === 'exams' ? 'bg-white text-slate-900 shadow' : 'text-slate-500 hover:text-slate-700'
          }`}
        >
          <BookOpen size={16} className="inline mr-1.5 -mt-0.5" /> Manage Exams
        </button>
      </div>

      {/* Tab: Manage Exams */}
      {tab === 'exams' && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
          <h2 className="text-lg font-semibold text-slate-800 mb-4">Exam List</h2>
          <div className="flex gap-2 mb-4">
            <input
              data-testid="new-exam-input"
              type="text"
              value={newExamName}
              onChange={e => setNewExamName(e.target.value)}
              placeholder="Enter exam name (e.g. SSC CGL 2026)"
              className="flex-1 px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              onKeyDown={e => e.key === 'Enter' && handleAddExam()}
            />
            <button
              data-testid="add-exam-btn"
              onClick={handleAddExam}
              className="px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 transition"
            >
              <Plus size={16} className="inline mr-1 -mt-0.5" /> Add
            </button>
          </div>
          <div className="divide-y divide-slate-100">
            {exams.length === 0 && <p className="text-slate-400 text-sm py-4 text-center">No exams added yet</p>}
            {exams.map(exam => (
              <div key={exam.id} className="flex items-center justify-between py-3">
                <div>
                  <p className="font-medium text-slate-800">{exam.name}</p>
                  <p className="text-xs text-slate-400">Added by {exam.created_by_name}</p>
                </div>
                <button
                  data-testid={`delete-exam-${exam.id}`}
                  onClick={() => handleDeleteExam(exam.id, exam.name)}
                  className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tab: Results */}
      {tab === 'results' && (
        <>
          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                data-testid="search-results"
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search by name, phone, or roll number..."
                className="w-full pl-10 pr-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              />
            </div>
            <div className="relative">
              <select
                data-testid="filter-exam"
                value={filterExam}
                onChange={e => setFilterExam(e.target.value)}
                className="appearance-none pl-3 pr-8 py-2 border border-slate-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              >
                <option value="">All Exams</option>
                {exams.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
              </select>
              <ChevronDown size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            </div>
            <div className="relative">
              <select
                data-testid="filter-stage"
                value={filterStage}
                onChange={e => setFilterStage(e.target.value)}
                className="appearance-none pl-3 pr-8 py-2 border border-slate-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              >
                <option value="">All Stages</option>
                <option value="written_cleared">Written Cleared</option>
                <option value="final_selection">Final Selection</option>
              </select>
              <ChevronDown size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            </div>
          </div>

          {/* Results Table */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            {loading ? (
              <div className="p-8 text-center text-slate-400">Loading results...</div>
            ) : results.length === 0 ? (
              <div className="p-8 text-center text-slate-400">
                <Award size={40} className="mx-auto mb-2 opacity-30" />
                <p>No results found. Add your first result entry!</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200">
                      <th className="text-left px-4 py-3 font-semibold text-slate-600">Name</th>
                      <th className="text-left px-4 py-3 font-semibold text-slate-600">Contact</th>
                      <th className="text-left px-4 py-3 font-semibold text-slate-600">Exam</th>
                      <th className="text-left px-4 py-3 font-semibold text-slate-600">Stage</th>
                      <th className="text-left px-4 py-3 font-semibold text-slate-600">Rank</th>
                      <th className="text-left px-4 py-3 font-semibold text-slate-600">Roll No.</th>
                      <th className="text-left px-4 py-3 font-semibold text-slate-600">Source</th>
                      <th className="text-left px-4 py-3 font-semibold text-slate-600">Document</th>
                      <th className="text-left px-4 py-3 font-semibold text-slate-600">Remarks</th>
                      <th className="text-right px-4 py-3 font-semibold text-slate-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {results.map(r => {
                      const stage = r.stage || 'written_cleared';
                      const badge = STAGE_BADGES[stage] || STAGE_BADGES.written_cleared;
                      const customCount = r.custom_fields
                        ? Object.values(r.custom_fields).filter(v => v !== '' && v !== null && v !== undefined).length
                        : 0;
                      return (
                      <tr key={r.id} className="hover:bg-slate-50 transition" data-testid={`result-row-${r.id}`}>
                        <td className="px-4 py-3 font-medium text-slate-900">
                          <div className="flex items-center gap-2">
                            <span>{r.name}</span>
                            {customCount > 0 && (
                              <button
                                onClick={() => setDetailsResult(r)}
                                data-testid={`custom-fields-badge-${r.id}`}
                                title={`${customCount} additional field${customCount === 1 ? '' : 's'} submitted — click to view`}
                                className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-purple-100 text-purple-700 hover:bg-purple-200 rounded-full text-[10px] font-bold transition"
                              >
                                <Sparkles size={10} /> +{customCount}
                              </button>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-slate-600 font-mono text-xs">{r.contact_number}</td>
                        <td className="px-4 py-3">
                          <span className="inline-block px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded text-xs font-medium">{r.exam_name}</span>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${badge.cls}`}>
                            {stage === 'final_selection' ? <Trophy size={10} /> : <CheckCircle size={10} />}
                            {badge.label}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-slate-600">{r.rank || '-'}</td>
                        <td className="px-4 py-3 text-slate-600">{r.roll_number || '-'}</td>
                        <td className="px-4 py-3 text-slate-500 text-xs">
                          {r.source === 'public_form' ? (
                            <span className="px-1.5 py-0.5 bg-purple-50 text-purple-700 rounded">Student</span>
                          ) : (r.verified_by || 'Admin')}
                        </td>
                        <td className="px-4 py-3">
                          {r.document_url ? (
                            <a href={r.document_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline text-xs flex items-center gap-1">
                              <ExternalLink size={12} /> {r.document_name || 'View'}
                            </a>
                          ) : <span className="text-slate-300 text-xs">-</span>}
                        </td>
                        <td className="px-4 py-3 text-slate-500 text-xs max-w-[150px] truncate">{r.remarks || '-'}</td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <button
                              onClick={() => setDetailsResult(r)}
                              data-testid={`view-details-${r.id}`}
                              className="p-1.5 text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 rounded transition"
                              title="View full submission details"
                            >
                              <Eye size={14} />
                            </button>
                            {stage !== 'final_selection' ? (
                              <button
                                onClick={() => handleStageChange(r, 'final_selection')}
                                data-testid={`mark-final-${r.id}`}
                                className="px-2 py-1 text-xs bg-emerald-50 text-emerald-700 rounded hover:bg-emerald-100 transition flex items-center gap-1 font-medium"
                                title="Mark as Final Selection"
                              >
                                <Trophy size={11} /> Mark Final
                              </button>
                            ) : (
                              <button
                                onClick={() => handleStageChange(r, 'written_cleared')}
                                className="px-2 py-1 text-xs bg-amber-50 text-amber-700 rounded hover:bg-amber-100 transition"
                                title="Revert to Written Cleared"
                              >
                                Revert
                              </button>
                            )}
                            <button
                              onClick={() => openEditForm(r)}
                              className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded transition"
                              title="Edit"
                            >
                              <FileText size={14} />
                            </button>
                            <button
                              data-testid={`delete-result-${r.id}`}
                              onClick={() => handleDeleteResult(r.id)}
                              className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded transition"
                              title="Delete"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination */}
            {pagination.total_pages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 bg-slate-50">
                <p className="text-xs text-slate-500">
                  Page {pagination.page} of {pagination.total_pages} ({pagination.total} total)
                </p>
                <div className="flex gap-2">
                  <button
                    disabled={pagination.page <= 1}
                    onClick={() => fetchResults(pagination.page - 1)}
                    className="px-3 py-1 text-xs border rounded disabled:opacity-40"
                  >
                    Previous
                  </button>
                  <button
                    disabled={pagination.page >= pagination.total_pages}
                    onClick={() => fetchResults(pagination.page + 1)}
                    className="px-3 py-1 text-xs border rounded disabled:opacity-40"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {/* Add/Edit Result Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto" data-testid="result-form-modal">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <h2 className="text-lg font-semibold text-slate-800">
                {editingResult ? 'Edit Result' : 'Add New Result'}
              </h2>
              <button onClick={resetForm} className="p-1 hover:bg-slate-100 rounded">
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  <User size={14} className="inline mr-1 -mt-0.5" /> Name *
                </label>
                <input
                  data-testid="result-name-input"
                  type="text"
                  value={formData.name}
                  onChange={e => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  <Phone size={14} className="inline mr-1 -mt-0.5" /> Contact Number *
                </label>
                <input
                  data-testid="result-contact-input"
                  type="text"
                  value={formData.contact_number}
                  onChange={e => setFormData({ ...formData, contact_number: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  <BookOpen size={14} className="inline mr-1 -mt-0.5" /> Exam *
                </label>
                <select
                  data-testid="result-exam-select"
                  value={formData.exam_id}
                  onChange={e => setFormData({ ...formData, exam_id: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  required
                >
                  <option value="">Select exam</option>
                  {exams.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    <Hash size={14} className="inline mr-1 -mt-0.5" /> Rank
                  </label>
                  <input
                    data-testid="result-rank-input"
                    type="text"
                    value={formData.rank}
                    onChange={e => setFormData({ ...formData, rank: e.target.value })}
                    placeholder="If available"
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    <Hash size={14} className="inline mr-1 -mt-0.5" /> Roll Number
                  </label>
                  <input
                    data-testid="result-roll-input"
                    type="text"
                    value={formData.roll_number}
                    onChange={e => setFormData({ ...formData, roll_number: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  <MessageSquare size={14} className="inline mr-1 -mt-0.5" /> Remarks
                </label>
                <textarea
                  data-testid="result-remarks-input"
                  value={formData.remarks}
                  onChange={e => setFormData({ ...formData, remarks: e.target.value })}
                  rows={2}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  <Upload size={14} className="inline mr-1 -mt-0.5" /> Document (Marksheet / Screenshot)
                </label>
                <input
                  data-testid="result-document-input"
                  type="file"
                  accept="image/*,.pdf"
                  onChange={e => setDocumentFile(e.target.files[0] || null)}
                  className="w-full text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-emerald-50 file:text-emerald-700 hover:file:bg-emerald-100"
                />
                {editingResult?.document_url && !documentFile && (
                  <p className="text-xs text-slate-400 mt-1">Current: {editingResult.document_name || 'Uploaded document'}</p>
                )}
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={resetForm} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg transition">
                  Cancel
                </button>
                <button
                  data-testid="result-submit-btn"
                  type="submit"
                  disabled={submitting}
                  className="px-6 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 transition disabled:opacity-50"
                >
                  {submitting ? 'Saving...' : editingResult ? 'Update' : 'Add Result'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* Result Details Modal — shows ALL student submitted fields including custom ones */}
      {detailsResult && (
        <ResultDetailsModal result={detailsResult} onClose={() => setDetailsResult(null)} />
      )}
    </div>
  );
};

export default Results;
